import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from 'src/common/utils/api';
import { FileStatus, FileUploadInput, UploadedFile } from './types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useUploadToStorage } from './useUploadToStorage';

export const useFileUploadActions = ({
  clientId: overrideClientId,
}: { clientId?: string } = {}) => {
  const { clientId: defaultClientId } = useCurrentClient();
  const clientId = overrideClientId ?? defaultClientId;
  const queryClient = useQueryClient();
  const { uploadToStorage, extractMetadata } = useUploadToStorage();
  const [error, setError] = useState<Error | null>(null);
  const resource = `clients/${clientId}/data-upload-files`;
  const fileListKey = ['clients', clientId, 'data-upload-files'];

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, string>({
    mutationFn: (fileId: string) => api.delete(`${resource}/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileListKey });
    },
  });

  const uploadFiles = async (files: FileUploadInput[]) => {
    try {
      const results = await Promise.all(
        files.map(async fileInput => {
          let documentDate = fileInput.documentDate?.split('T')[0];

          if (fileInput.file) {
            const uploadResult = await uploadToStorage(fileInput.file, clientId);
            if (!documentDate) {
              const metadata = await extractMetadata(uploadResult.location, fileInput.file);
              if (metadata.creationDate) {
                documentDate = metadata.creationDate;
              }
            }
          }

          return api.post<UploadedFile>(resource, {
            fileName: fileInput.fileName || fileInput.file?.name,
            fileSize: fileInput.file?.size || 0,
            fileType: fileInput.file?.type || 'FOLDER',
            isFolder: fileInput.isFolder || false,
            isResolved: fileInput.isResolved || false,
            isTemporary: fileInput.isTemporary || false,
            modelId: fileInput.modelId || undefined,
            parentId: fileInput.parentId,
            documentDate,
          });
        }),
      );
      queryClient.invalidateQueries({ queryKey: fileListKey });
      return results;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to upload files'));
      throw err;
    }
  };

  const deleteFile = async (fileId: string) => {
    await deleteFileMutation(fileId);
  };

  const setFileResolved = async (file: UploadedFile, isResolved: boolean) => {
    try {
      await api.put(`${resource}/${file.id}`, {
        isResolved,
        isFiled: false,
        parentId: file.parentId ?? null,
        fileName: null,
      });
      queryClient.invalidateQueries({ queryKey: fileListKey });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update file'));
      throw err;
    }
  };

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await api.put(`${resource}/${file.id}`, {
        parentId: parentId ?? null,
        isResolved: file.isResolved,
        isFiled: true,
        fileName: null,
      });
      queryClient.invalidateQueries({ queryKey: fileListKey });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to move file'));
      throw err;
    }
  };

  const createFolder = async (name: string, parentId?: string) => {
    try {
      await api.post(resource, {
        fileName: name,
        fileSize: 0,
        fileType: 'FOLDER',
        isFolder: true,
        isResolved: true,
        parentId,
      });
      queryClient.invalidateQueries({ queryKey: fileListKey });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create folder'));
      throw err;
    }
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    try {
      await api.put(`${resource}/${file.id}`, {
        isResolved: file.isResolved,
        isFiled: false,
        parentId: file.parentId ?? null,
        fileName: newName,
      });
      queryClient.invalidateQueries({ queryKey: fileListKey });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to rename folder'));
      throw err;
    }
  };

  const getFileStatus = (file: UploadedFile): FileStatus => {
    const status = file.backgroundProcess?.status;
    if (status === 'SUCCESS' || !status) {
      return { color: 'green.6', label: 'Ready' };
    } else if (status === 'FAILED') {
      return { color: 'red.6', label: 'Failed' };
    } else {
      return {
        color: 'blue.6',
        label: 'Processing',
        showLoader: true,
      };
    }
  };

  return {
    uploadFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    setFileResolved,
    moveFile,
    createFolder,
    renameFolder,
    getFileStatus,
    error,
  };
};
